import {useTransition} from '@yandex-delivery/blocks'
import {externalApiURL} from '@yandex-delivery/client-core/api/corp'
import type {ServerError} from '@yandex-delivery/core/utils/network'
import {useCallback, useId, useMemo, useState} from 'react'
import {useFormContext} from 'react-hook-form'

import {useConfigContext} from '../../../../../../utils/contexts/ConfigContext'
import type {
  CallbackHandlers,
  OptimizeRouteResultResponse,
  OptimizeRouteStartRequest,
  Point,
  RouteOptimizationState,
} from './types'
import {fetchOptimizedRoute, toDestinationRouteChangePoint, toSourceRouteChangePoint} from './utils'

export function useOptimizeRouteConfirmModal(setActive?: (isActive: boolean) => void): {
  optimizationState: RouteOptimizationState
  onDecline: VoidFunction
  onAccept: VoidFunction
} {
  const [optimizationState, setOptimizationState] = useState<RouteOptimizationState>('idle')

  const {watch} = useFormContext()
  const routePoints = watch('route')
  const destinationPoints: Point[] = Object.keys(routePoints).flatMap(key =>
    key === 'source' || key === 'return' ? [] : {...routePoints[key], _id: key},
  )
  const {fetchOptimisedRouteHandler} = useFetchOptimizedRoute()
  const {destinationPointsUpdateHandler} = useUpdateDestinationPoints()

  const successFetchOptimizationRouteHandler = useCallback(
    (route?: OptimizeRouteResultResponse) => {
      destinationPointsUpdateHandler(route?.result?.route)
      setOptimizationState('completed')
      setActive?.(false)
    },
    [destinationPointsUpdateHandler, setActive],
  )

  const route: Point[] = useMemo(() => {
    const sourcePoint: Point = {...routePoints.source, _id: 'source'}
    return [toSourceRouteChangePoint(sourcePoint), ...destinationPoints.map(toDestinationRouteChangePoint)]
  }, [destinationPoints, routePoints])

  const onAccept = useCallback(() => {
    fetchOptimisedRouteHandler(
      {
        route,
      },
      {
        onPending: () => {
          setOptimizationState('pending')
        },
        onError: () => {
          setOptimizationState('error')
        },
        onSuccess: successFetchOptimizationRouteHandler,
      },
    )
  }, [fetchOptimisedRouteHandler, route, successFetchOptimizationRouteHandler])

  const onDecline = useCallback(() => {
    setActive?.(false)
  }, [setActive])

  return {
    optimizationState,
    onDecline,
    onAccept,
  }
}

export function useFetchOptimizedRoute() {
  const key = useId()
  const {clientId = '', countryCode} = useConfigContext()
  const [{payload: optimisedRoute, isPending: isOptimisedRoutePending}, startTransition] = useTransition<
    OptimizeRouteResultResponse,
    ServerError
  >(undefined, {
    key: `optimised-route-${key}`,
  })

  const fetchOptimisedRouteHandler = useCallback(
    (body: OptimizeRouteStartRequest, handlers: CallbackHandlers) => {
      handlers.onPending()

      startTransition(
        () => {
          return fetchOptimizedRoute(clientId, body, countryCode, externalApiURL)
        },
        {
          onCommit: response => {
            handlers.onSuccess(response)
          },
          onError: () => {
            handlers.onError()
          },
        },
      )
    },
    [countryCode, clientId],
  )

  return {
    optimisedRoute,
    isOptimisedRoutePending,
    fetchOptimisedRouteHandler,
  }
}

function useUpdateDestinationPoints() {
  const {watch, setValue} = useFormContext()
  const name = 'route'

  function destinationPointsUpdateHandler(route?: string[]) {
    const routePoints = watch(name)
    const contacts = watch('contacts')
    const sourceContact = contacts.find(contact => contact.pointId === 'source')

    const newRoute = {source: routePoints.source, return: routePoints.return}
    const newContacts = [sourceContact] // только сорс, потому что контакты для return лежат отдельно
    route?.forEach((pointId: string, index: number) => {
      if (pointId === 'source' || pointId === 'destination') return

      /** переименовываем точки и связанные с ними контакты */
      const newName = `destination_${index}`
      newRoute[newName] = routePoints[pointId]

      const newContact = contacts.find(contact => contact.pointId === pointId)
      newContact.pointId = newName
      newContacts.push(newContact)
    })
    setValue(name, newRoute)
    setValue('contacts', newContacts)

    /** индикатор для конфига кнопки оптимизации маршрута, чтобы рисовать там галочку или стрелочку */
    setValue('is_route_optimized', true)
  }
  return {
    destinationPointsUpdateHandler,
  }
}
